/**
 * IPRanker SDK - Main Class
 * Single API call for IP Intelligence analysis with automatic fingerprint + behavior collection
 */

import { FingerprintCollector, BehaviorCollector, DeviceCollector } from './collectors';
import { APIClient } from './api/client';
import type { IPRankerOptions, AnalysisResult, AnalyzeOptions, EventCallbacks } from './types';

/**
 * API version this SDK targets.
 * Each major SDK release is built for a specific API version.
 * - SDK v1.x → API v1
 * - SDK v2.x → API v2 (future)
 */
const API_VERSION = 'v1';

export class IPRanker {
  private apiKey: string;
  private apiClient: APIClient;
  private fingerprintCollector: FingerprintCollector;
  private behaviorCollector: BehaviorCollector;
  private deviceCollector: DeviceCollector;
  private options: Required<Omit<IPRankerOptions, 'onReady' | 'onAnalyzing' | 'onComplete' | 'onError'>>;
  private callbacks: EventCallbacks;

  constructor(apiKey: string, options: Partial<IPRankerOptions> = {}) {
    // Validate API key
    if (!apiKey || typeof apiKey !== 'string' || apiKey.length < 10) {
      throw new Error('Invalid API key. Please provide a valid IPRanker API key.');
    }

    this.apiKey = apiKey;

    // Set default options
    this.options = {
      baseURL: options.baseURL || 'https://api.ipranker.com',
      collectBehavior: options.collectBehavior !== false, // default true
      behaviorTimeout: options.behaviorTimeout || 5000, // 5 seconds
      autoRetry: options.autoRetry !== false, // default true
      maxRetries: options.maxRetries || 3,
      cache: options.cache !== false, // default true
      cacheTimeout: options.cacheTimeout || 300000, // 5 minutes
      debug: options.debug || false
    };

    // Set callbacks
    this.callbacks = {
      onReady: options.onReady,
      onAnalyzing: options.onAnalyzing,
      onComplete: options.onComplete,
      onError: options.onError
    };

    // Initialize components with versioned API paths
    // baseURL should NOT include version (e.g., 'https://api.ipranker.com' or 'http://localhost:4001/api')
    const versionedBaseURL = `${this.options.baseURL}/${API_VERSION}`;
    this.apiClient = new APIClient(versionedBaseURL, this.apiKey);
    const fingerprintEndpoint = `${versionedBaseURL}/fingerprint-visitor-id`;
    this.fingerprintCollector = new FingerprintCollector(fingerprintEndpoint, this.apiKey);
    this.behaviorCollector = new BehaviorCollector();
    this.deviceCollector = new DeviceCollector();

    // Initialize collectors
    this._initialize();
  }

  /**
   * Initialize SDK
   */
  private _initialize(): void {
    try {
      // Start collecting behavioral data in background
      if (this.options.collectBehavior) {
        this.behaviorCollector.start();
      }

      this._log('IPRanker SDK initialized');
      this.callbacks.onReady?.();
    } catch (error) {
      this._handleError(error as Error);
    }
  }

  /**
   * Analyze current visitor - main method
   * Collects fingerprint + behavioral data and sends to IPRanker API
   */
  public async analyze(customOptions?: AnalyzeOptions): Promise<AnalysisResult> {
    try {
      this._log('Starting analysis...');
      this.callbacks.onAnalyzing?.();

      // Get IP for cache key (use provided IP or 'auto' for auto-detected)
      const cacheKey = customOptions?.ip || 'auto';

      // Check cache first (per IP address)
      if (this.options.cache) {
        const cached = this._getCachedResult(cacheKey);
        if (cached) {
          this._log('Returning cached result for IP:', cacheKey);
          this.callbacks.onComplete?.(cached);
          return cached;
        }
      }

      // Step 1: Collect fingerprint data
      this._log('Collecting fingerprint data...');
      const fingerprintData = await this.fingerprintCollector.collect();

      // Step 2: Collect behavioral data (with timeout)
      this._log('Collecting behavioral data...');
      const behaviorData = this.options.collectBehavior
        ? await this._collectBehaviorWithTimeout(customOptions?.timeout || this.options.behaviorTimeout)
        : null;

      // Step 3: Collect device data
      this._log('Collecting device data...');
      const deviceData = await this.deviceCollector.collect();

      // Step 4: Prepare request payload
      const payload: any = {
        fingerprint: fingerprintData,
        behavior: behaviorData,
        device: deviceData,
        includeRawData: customOptions?.includeRawData || false
      };

      // Only include IP if explicitly provided (otherwise backend will auto-detect from request)
      if (customOptions?.ip) {
        payload.ip = customOptions.ip;
      }

      this._log('Sending analysis request to IPRanker API...', { ip: payload.ip, hasIp: !!payload.ip });

      // Step 5: Send to IPRanker API (with auto-retry)
      const result = await this._sendWithRetry(payload);

      // Step 6: Cache result (per IP address)
      if (this.options.cache) {
        this._cacheResult(cacheKey, result);
      }

      this._log('Analysis complete', result);
      this.callbacks.onComplete?.(result);
      return result;

    } catch (error) {
      this._handleError(error as Error);
      throw error;
    }
  }

  /**
   * Get fingerprint data only (without full analysis)
   */
  public async getFingerprint() {
    return await this.fingerprintCollector.collect();
  }

  /**
   * Get behavioral data only
   */
  public getBehaviorData() {
    return this.behaviorCollector.getData();
  }

  /**
   * Get device data only
   */
  public async getDeviceData() {
    return await this.deviceCollector.collect();
  }

  /**
   * Clear cache
   */
  public clearCache(): void {
    this._clearCache();
    this._log('Cache cleared');
  }

  /**
   * Stop behavioral data collection and cleanup
   */
  public destroy(): void {
    this.behaviorCollector.stop();
    this._log('SDK destroyed');
  }

  // ========== Private Methods ==========

  /**
   * Collect behavioral data with timeout
   */
  private async _collectBehaviorWithTimeout(timeout: number): Promise<any> {
    return Promise.race([
      Promise.resolve(this.behaviorCollector.getData()),
      new Promise(resolve => setTimeout(() => resolve(null), timeout))
    ]);
  }

  /**
   * Send request with auto-retry
   */
  private async _sendWithRetry(payload: any, retries = 0): Promise<AnalysisResult> {
    try {
      return await this.apiClient.analyze(payload);
    } catch (error) {
      if (this.options.autoRetry && retries < this.options.maxRetries) {
        this._log(`Retry ${retries + 1}/${this.options.maxRetries} after error:`, error);
        await this._delay(1000 * (retries + 1)); // Exponential backoff
        return this._sendWithRetry(payload, retries + 1);
      }
      throw error;
    }
  }

  /**
   * Get cached result if available and not expired (per IP address)
   */
  private _getCachedResult(ip: string): AnalysisResult | null {
    try {
      const cacheData = localStorage.getItem('ipranker_cache');
      if (!cacheData) return null;

      const cache = JSON.parse(cacheData);

      // Migration: Clear old cache format (has 'result' and 'timestamp' at root level)
      if (cache.result && cache.timestamp) {
        this._log('Clearing old cache format');
        localStorage.removeItem('ipranker_cache');
        return null;
      }

      const entry = cache[ip];
      if (!entry || !entry.result || !entry.timestamp) return null;

      const age = Date.now() - entry.timestamp;

      if (age < this.options.cacheTimeout) {
        return entry.result;
      }

      // Remove expired entry
      delete cache[ip];
      localStorage.setItem('ipranker_cache', JSON.stringify(cache));
      return null;
    } catch (e) {
      // If parsing fails, clear the cache
      localStorage.removeItem('ipranker_cache');
      return null;
    }
  }

  /**
   * Cache result to localStorage (per IP address)
   */
  private _cacheResult(ip: string, result: AnalysisResult): void {
    try {
      let cache: Record<string, { result: AnalysisResult; timestamp: number }> = {};

      const existing = localStorage.getItem('ipranker_cache');
      if (existing) {
        cache = JSON.parse(existing);
      }

      cache[ip] = {
        result,
        timestamp: Date.now()
      };

      localStorage.setItem('ipranker_cache', JSON.stringify(cache));
    } catch (e) {
      // Cache failed, ignore
      this._log('Cache failed:', e);
    }
  }

  /**
   * Clear cache from localStorage
   */
  private _clearCache(): void {
    try {
      localStorage.removeItem('ipranker_cache');
    } catch (e) {}
  }

  /**
   * Handle errors
   */
  private _handleError(error: Error): void {
    this._log('Error:', error);
    this.callbacks.onError?.(error);
  }

  /**
   * Debug logging
   */
  private _log(...args: any[]): void {
    if (this.options.debug) {
      console.log('[IPRanker SDK]', ...args);
    }
  }

  /**
   * Delay utility
   */
  private _delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default IPRanker;
